'use client'

import { Check, X } from 'lucide-react'
import * as React from 'react'

import { Input } from '../input'
import { cn } from '../lib/cn'
import { Popover, PopoverAnchor, PopoverContent } from '../popover'
import { ScrollArea } from '../scroll-area'
import { Spin } from '../spin'

const overflowEllipsis = 'overflow-hidden text-ellipsis whitespace-nowrap'
const pulseBorder = '!animate-border-pulse'

export type SearchSelectOption<T> = {
  id: T
  label: string
  description?: string | null
}

export interface SearchSelectProps<T> {
  value: T | null
  inputText: string
  onInputChange: (text: string) => void
  onSelect: (id: T | null) => void
  options: SearchSelectOption<T>[]
  open: boolean
  onOpenChange: (open: boolean) => void
  placeholder?: string
  isLoading?: boolean
  isPending?: boolean
  emptyText?: string
  onInteractOutside?: () => void
  inputRef?: React.Ref<HTMLInputElement>
  className?: string
}

function SearchSelect<T>({
  value,
  inputText,
  onInputChange,
  onSelect,
  options,
  open,
  onOpenChange,
  placeholder,
  isLoading = false,
  isPending = false,
  emptyText = 'No results',
  onInteractOutside,
  inputRef,
  className,
}: SearchSelectProps<T>) {
  const handleOpen = () => onOpenChange(true)
  const handleClose = () => {
    onOpenChange(false)
    onInteractOutside?.()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value)

  const handleClear = () => onSelect(null)

  const handleSelect = (option: SearchSelectOption<T>) => () => {
    if (option.id === value) {
      onSelect(null)

      return
    }
    onSelect(option.id)
    onOpenChange(false)
  }

  return (
    <Popover open={open}>
      <PopoverAnchor>
        <div className={cn('relative', className)}>
          <Input
            ref={inputRef}
            type='text'
            value={inputText}
            onChange={handleInputChange}
            onFocus={handleOpen}
            onClick={handleOpen}
            placeholder={placeholder}
            autoComplete='off'
            className={cn('pr-7!', overflowEllipsis)}
          />

          {isPending && (
            <div className='absolute right-[6px] top-1/2 -translate-y-1/2'>
              <Spin size='md' strokeWidth={1} />
            </div>
          )}

          {!isPending && inputText && (
            <button
              type='button'
              aria-label='Clear'
              onClick={handleClear}
              className='absolute right-[10px] top-1/2 -translate-y-1/2 hover:cursor-pointer hover:text-primary'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </div>
      </PopoverAnchor>

      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={handleClose}
        className='w-fit min-w-(--radix-popover-trigger-width) bg-background p-2'
      >
        <ScrollArea autoPadding type='always' className='animate-fade-in flex max-h-96 flex-col gap-2!'>
          {options.length > 0 &&
            options.map((option) => (
              <div
                key={String(option.id)}
                tabIndex={0}
                role='option'
                aria-selected={option.id === value}
                onClick={handleSelect(option)}
                className={cn(
                  'my-1 flex cursor-pointer flex-row items-center justify-between rounded-md border border-input bg-card px-2 py-1 text-sm hover:bg-input',
                  isLoading && pulseBorder,
                  option.id === value && 'border-primary',
                )}
              >
                <div className={cn('flex flex-col gap-0.5', overflowEllipsis)}>
                  <div className={cn('font-semibold', overflowEllipsis)}>{option.label}</div>
                  {option.description && (
                    <div className={cn('text-xs opacity-70', overflowEllipsis)}>{option.description}</div>
                  )}
                </div>
                {option.id === value && <Check strokeWidth={2} className='ml-2 h-4 w-4 min-w-4 text-primary' />}
              </div>
            ))}

          {options.length === 0 && (
            <div
              className={cn(
                'flex h-12 items-center justify-center rounded-md border border-input p-2 text-center text-sm text-muted-foreground',
                isLoading && pulseBorder,
              )}
            >
              {isPending ? <Spin size='md' /> : emptyText}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

export { SearchSelect }
